import { ArrowLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    CartesianGrid, Legend, Line, LineChart,
    ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { getProfitChart, getProfitChartCustom, getTopServices } from '../../../api/report.api';
import styles from '../../../assets/scss/pages/ReportSales.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatCurrency } from '../../../utils/formatters';

const PERIODS = [
    { key: 'today', label: 'Hôm nay' },
    { key: 'yesterday', label: 'Hôm qua' },
    { key: '7days', label: '7 ngày' },
    { key: 'thismonth', label: 'Tháng này' },
    { key: 'lastmonth', label: 'Tháng trước' },
    { key: 'thisquarter', label: 'Quý này' },
    { key: 'thisyear', label: 'Năm này' },
    { key: 'custom', label: 'Tuỳ chọn' },
];

const VIEW_MODES = [
    { key: 'day', label: 'Theo ngày' },
    { key: 'week', label: 'Theo tuần' },
    { key: 'month', label: 'Theo tháng' },
];

function fmtDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
}

function shortCurrency(val) {
    if (val >= 1000000) return `${(val / 1000000).toFixed(1).replace(/\.0$/, '')} tr`;
    if (val >= 1000) return `${(val / 1000).toFixed(0)}k`;
    return String(val);
}

export default function ReportSales() {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canView = hasPermission(PERMISSIONS.REPORT.SALES_VIEW);
    const canViewDetail = hasPermission(PERMISSIONS.REPORT.SALES_DETAIL_VIEW);
    const canViewRevenue = hasPermission(PERMISSIONS.REPORT.REVENUE_VIEW);
    const canViewCustomer = hasPermission(PERMISSIONS.REPORT.CUSTOMER_VIEW);
    const canViewBills = hasPermission(PERMISSIONS.BILL.VIEW_LIST);
    const canViewService = hasPermission(PERMISSIONS.SERVICE.VIEW_DETAIL);

    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('7days');
    const [viewMode, setViewMode] = useState('day');
    const [startDate, setStartDate] = useState(fmtDate(new Date()));
    const [endDate, setEndDate] = useState(fmtDate(new Date()));
    const [chartData, setChartData] = useState([]);
    const [summary, setSummary] = useState({
        totalProfit: 0, totalRevenue: 0, totalCost: 0, totalCustomers: 0, totalOrders: 0,
    });
    const [topServices, setTopServices] = useState([]);

    const fetchIdRef = useRef(0);

    const fetchData = useCallback(async () => {
        if (!canView) { setLoading(false); return; }
        const fetchId = ++fetchIdRef.current;
        setLoading(true);
        try {
            let chartRes;
            if (period === 'custom') {
                chartRes = await getProfitChartCustom({ startDate, endDate });
            } else {
                chartRes = await getProfitChart({ viewMode, period });
            }
            if (fetchId !== fetchIdRef.current) return;
            setChartData(chartRes.data?.chartData || []);
            setSummary({
                totalProfit: chartRes.data?.totalProfit || 0,
                totalRevenue: chartRes.data?.totalRevenue || 0,
                totalCost: chartRes.data?.totalCost || 0,
                totalCustomers: chartRes.data?.totalCustomers || 0,
                totalOrders: chartRes.data?.totalOrders || 0,
            });

            const topParams = period === 'custom'
                ? { period: 'today', startDate, endDate }
                : { period };
            const topRes = await getTopServices(topParams);
            if (fetchId !== fetchIdRef.current) return;
            setTopServices(Array.isArray(topRes.data) ? topRes.data.slice(0, 3) : []);
        } catch (err) {
            if (fetchId !== fetchIdRef.current) return;
            console.error('Error fetching sales report:', err);
        } finally {
            if (fetchId === fetchIdRef.current) setLoading(false);
        }
    }, [canView, period, viewMode, startDate, endDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Auto-adjust viewMode like app
    const handlePeriodChange = (p) => {
        setPeriod(p);
        if (p === 'custom' || p === 'today' || p === 'yesterday') setViewMode('day');
        else if ((p === 'thisquarter' || p === 'thisyear') && viewMode === 'day') setViewMode('week');
    };

    const handleStartDateChange = (e) => {
        const val = e.target.value;
        setStartDate(val);
        if (val > endDate) setEndDate(val);
    };

    const handleEndDateChange = (e) => {
        const val = e.target.value;
        setEndDate(val);
        if (val < startDate) setStartDate(val);
    };

    const isDayDisabled = period === 'thisquarter' || period === 'thisyear';

    // Navigate helpers — truyền period/date params giống app
    const buildQuery = () => {
        if (period === 'thisquarter' || period === 'thisyear') {
            // Tính khoảng ngày thực cho quý/năm
            const now = new Date();
            let sd;
            if (period === 'thisquarter') {
                const qStart = Math.floor(now.getMonth() / 3) * 3;
                sd = `${now.getFullYear()}-${String(qStart + 1).padStart(2, '0')}-01`;
            } else {
                sd = `${now.getFullYear()}-01-01`;
            }
            return `?period=custom&startDate=${sd}&endDate=${fmtDate(now)}`;
        }
        if (period === 'custom') return `?period=custom&startDate=${startDate}&endDate=${endDate}`;
        return `?period=${period}`;
    };
    const goDetail = () => canViewDetail && navigate(`/admin/reports/products-detail${buildQuery()}`);
    const goRevenue = () => canViewRevenue && navigate(`/admin/reports/revenue${buildQuery()}`);
    const goCustomer = () => canViewCustomer && navigate(`/admin/reports/customers${buildQuery()}`);
    const goBills = () => {
        if (!canViewBills) return;
        if (period === 'thisquarter' || period === 'thisyear' || period === 'custom') {
            const now = new Date();
            let sd = startDate, ed = endDate;
            if (period === 'thisquarter') {
                const qStart = Math.floor(now.getMonth() / 3) * 3;
                sd = `${now.getFullYear()}-${String(qStart + 1).padStart(2, '0')}-01`;
                ed = fmtDate(now);
            } else if (period === 'thisyear') {
                sd = `${now.getFullYear()}-01-01`;
                ed = fmtDate(now);
            }
            navigate(`/admin/bills?startDate=${sd}&endDate=${ed}`);
        } else {
            navigate(`/admin/bills?period=${period}`);
        }
    };
    const goService = (id) => canViewService && navigate(`/admin/services/${id}`);

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <button className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ArrowLeft />
                </button>
                <h1 className={styles.title}>Báo cáo bán hàng</h1>
            </div>

            {/* Filters */}
            <div className={styles.filters}>
                <div className={styles.periodTabs}>
                    {PERIODS.map((p) => (
                        <button
                            key={p.key}
                            className={`${styles.periodTab}${period === p.key ? ` ${styles.periodTabActive}` : ''}`}
                            onClick={() => handlePeriodChange(p.key)}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>

                {period === 'custom' && (
                    <div className={styles.customDateRow}>
                        <div className={styles.dateGroup}>
                            <span className={styles.dateLabel}>Từ ngày:</span>
                            <input type="date" className={styles.dateInput} value={startDate} onChange={handleStartDateChange} />
                        </div>
                        <div className={styles.dateGroup}>
                            <span className={styles.dateLabel}>Đến ngày:</span>
                            <input type="date" className={styles.dateInput} value={endDate} onChange={handleEndDateChange} />
                        </div>
                    </div>
                )}

                {period !== 'custom' && (
                    <div className={styles.viewModeTabs}>
                        {VIEW_MODES.map((v) => (
                            <button
                                key={v.key}
                                className={`${styles.viewModeTab}${viewMode === v.key ? ` ${styles.viewModeTabActive}` : ''}`}
                                onClick={() => !((v.key === 'day') && isDayDisabled) && setViewMode(v.key)}
                                disabled={v.key === 'day' && isDayDisabled}
                                style={v.key === 'day' && isDayDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                            >
                                {v.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {loading ? (
                <div className={styles.loading}><div className={styles.spinner} />Đang tải dữ liệu...</div>
            ) : (
                <>
                    {/* Summary boxes */}
                    <div className={styles.summaryGrid}>
                        <div className={`${styles.summaryCard} ${styles.summaryGreen}`} onClick={goDetail} style={{ cursor: canViewDetail ? 'pointer' : 'default' }}>
                            <span className={styles.summaryLabel}>Lợi nhuận</span>
                            <span className={styles.summaryValue}>{formatCurrency(summary.totalProfit)}</span>
                            {canViewDetail && <ChevronRight size={16} className={styles.summaryArrow} />}
                        </div>
                        <div className={`${styles.summaryCard} ${styles.summaryBlue}`} onClick={goRevenue} style={{ cursor: canViewRevenue ? 'pointer' : 'default' }}>
                            <span className={styles.summaryLabel}>Doanh thu</span>
                            <span className={styles.summaryValue}>{formatCurrency(summary.totalRevenue)}</span>
                            {canViewRevenue && <ChevronRight size={16} className={styles.summaryArrow} />}
                        </div>
                        <div className={`${styles.summaryCard} ${styles.summaryOrange}`}>
                            <span className={styles.summaryLabel}>Giá vốn</span>
                            <span className={styles.summaryValue}>{formatCurrency(summary.totalCost)}</span>
                        </div>
                    </div>

                    <div className={styles.statsRow}>
                        <div className={styles.statBox} onClick={goCustomer} style={{ cursor: canViewCustomer ? 'pointer' : 'default' }}>
                            <span className={styles.statLabel}>Khách hàng</span>
                            <span className={styles.statValue}>{summary.totalCustomers}</span>
                            {canViewCustomer && <ChevronRight size={14} className={styles.statArrow} />}
                        </div>
                        <div className={styles.statBox} onClick={goBills} style={{ cursor: canViewBills ? 'pointer' : 'default' }}>
                            <span className={styles.statLabel}>Đơn hàng</span>
                            <span className={styles.statValue}>{summary.totalOrders}</span>
                            {canViewBills && <ChevronRight size={14} className={styles.statArrow} />}
                        </div>
                    </div>

                    {/* Profit chart */}
                    <div className={styles.chartCard}>
                        <div className={styles.chartTitle}>Lợi nhuận</div>
                        {chartData.length === 0 ? (
                            <div className={styles.empty}>Không có dữ liệu</div>
                        ) : (
                            <div className={styles.chartWrap}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#ECEEF2" />
                                        <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#ECEEF2' }} />
                                        <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={false} tickFormatter={shortCurrency} />
                                        <Tooltip
                                            contentStyle={{ borderRadius: 10, border: '1px solid #ECEEF2', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: 13 }}
                                            formatter={(value, name) => {
                                                const labels = { profit: 'Lợi nhuận', revenue: 'Doanh thu', cost: 'Giá vốn' };
                                                return [formatCurrency(value), labels[name] || name];
                                            }}
                                        />
                                        <Legend formatter={(v) => ({ profit: 'Lợi nhuận', revenue: 'Doanh thu', cost: 'Giá vốn' }[v] || v)} />
                                        <Line type="monotone" dataKey="revenue" stroke="#0098F6" strokeWidth={2} dot={false} />
                                        <Line type="monotone" dataKey="profit" stroke="#22C55E" strokeWidth={2} dot={false} />
                                        <Line type="monotone" dataKey="cost" stroke="#F59E0B" strokeWidth={2} dot={false} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>

                    {/* Top 3 services */}
                    {topServices.length > 0 && (
                        <div className={styles.topCard}>
                            <div className={styles.topTitle}>Top 3 dịch vụ bán chạy</div>
                            {topServices.map((svc, idx) => (
                                <div
                                    key={svc.serviceId || idx}
                                    className={styles.topItem}
                                    onClick={() => goService(svc.serviceId)}
                                    style={{ cursor: canViewService ? 'pointer' : 'default' }}
                                >
                                    <span className={styles.topRank}>{idx + 1}</span>
                                    {svc.serviceImage && (
                                        <img src={svc.serviceImage} alt={svc.serviceName} className={styles.topImg} />
                                    )}
                                    <div className={styles.topInfo}>
                                        <div className={styles.topName}>
                                            {svc.serviceName}
                                            {svc.variantLabel && <span className={styles.topVariant}> - {svc.variantLabel}</span>}
                                        </div>
                                        <div className={styles.topQty}>{svc.totalQuantity} lượt bán</div>
                                    </div>
                                    {canViewService && <ChevronRight size={16} style={{ color: '#B0B7C3', flexShrink: 0 }} />}
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
